export type SearchSupportedRepubName = 'GZ' | 'OC' | 'US' | 'WS';

const DEFAULT_POSITIVE_EV_SORT_ORDER = [
  'expectedValue',
  'aiProbability',
  'sportsbookProbability',
  'eventTime',
  'bestOdds',
  'hitRate',
] as const;

export type PositiveEvSortGroup = (typeof DEFAULT_POSITIVE_EV_SORT_ORDER)[number];

const POSITIVE_EV_SORT_ORDER_OVERRIDES: Partial<Record<SearchSupportedRepubName, readonly PositiveEvSortGroup[]>> = {
  GZ: ['aiProbability', 'expectedValue', 'sportsbookProbability', 'eventTime', 'bestOdds', 'hitRate'] as const,
};

export function getPositiveEvSortOrder(repubName: string): readonly PositiveEvSortGroup[] {
  return POSITIVE_EV_SORT_ORDER_OVERRIDES[repubName as SearchSupportedRepubName] ?? DEFAULT_POSITIVE_EV_SORT_ORDER;
}

export type FiltersConfig = {
  bestLine?: string;
  bestMarket?: string;
  bestOdds: string;
  betType: string;
  date: string;
  loadLess: string;
  loadMore: string;
  matchCenterSearchPlaceholder: string;
  middleBetLine: string;
  minimumAIProb: string;
  minimumEV: string;
  minimumHitRate: string;
  odds: string;
  player: string;
  propsFilter: string;
  sortAIProbHL: string;
  sortAIProbLH: string;
  sortBestOddsHL: string;
  sortBestOddsLH: string;
  sortBetPercentageHL: string;
  sortBetPercentageLH: string;
  sortEVHL: string;
  sortEVLH: string;
  sortEventTime: string;
  sortHitRate10HL: string;
  sortHitRate10LH: string;
  sortHitRate20HL: string;
  sortHitRate20LH: string;
  sortHitRate5HL: string;
  sortHitRate5LH: string;
  sortImProbHL: string;
  sortImProbLH: string;
  sortStakeBetDiffHL: string;
  sortStakeBetDiffLH: string;
  sortStakePercentageHL: string;
  sortStakePercentageLH: string;
  sortSubEventBetTime: string;
  team: string;
};

/**
 * Filters configurations for each republic
 */
export const FILTERS_CONFIGS = {
  GZ: {
    bestMarket: 'Miglior {market}',
    bestLine: 'Miglior Quota {line}',
    propsFilter: 'Giocate Speciali',
    minimumEV: 'EV minimo',
    minimumAIProb: 'Probabilità minima per l`IA',
    minimumHitRate: '% min di successo (ultime 10)',
    date: 'Data',
    odds: 'Cuotas',
    bestOdds: 'Migliori quote',
    player: 'Giocatore',
    loadMore: 'Carica Altro',
    loadLess: 'Carica Meno',
    matchCenterSearchPlaceholder: 'Cerca nome squadra',
    middleBetLine: 'PAREGGIO',
    betType: 'Mercato',
    sortEventTime: 'Orario Evento',
    sortEVHL: 'Expected Value alto - basso',
    sortEVLH: 'Expected Value basso - alto',
    sortAIProbHL: 'Probabilità IA alta - bassa',
    sortAIProbLH: 'Probabilità IA bassa - alta',
    sortImProbHL: 'Probabilità implicita alta - basso',
    sortImProbLH: 'Probabilità implicita bassa - alta',
    sortBestOddsHL: 'Migliori quote: alta - bassa',
    sortBestOddsLH: 'Migliori quote: bassa - alta',
    sortHitRate5HL: '% successo (ultime 5) alta - bassa',
    sortHitRate5LH: '% successo (ultime 5) bassa - alta',
    sortHitRate10HL: '% successo (ultime 10) alta - bassa',
    sortHitRate10LH: '% successo (ultime 10) bassa - alta',
    sortHitRate20HL: '% successo (ultime 20) alta - bassa',
    sortHitRate20LH: '% successo (ultime 20) bassa - alta',
    sortSubEventBetTime: 'Orario di inizio',
    sortBetPercentageHL: '% of Bets High-Low',
    sortBetPercentageLH: '% of Bets Low-High',
    sortStakePercentageHL: '% of Money High-Low',
    sortStakePercentageLH: '% of Money Low-High',
    sortStakeBetDiffHL: 'Difference High-Low',
    sortStakeBetDiffLH: 'Difference Low-High',
    team: 'Squadra',
  },
  OC: {
    bestMarket: 'Best {market}',
    bestLine: 'Best {line} Line',
    propsFilter: 'Props',
    minimumEV: 'Minimum EV',
    minimumAIProb: 'Minimum AI Probability',
    minimumHitRate: 'Minimum Hit Rate (Last 10)',
    date: 'Dates',
    odds: 'Odds',
    bestOdds: 'Best Odds',
    team: 'Team',
    player: 'Player',
    loadMore: 'Load more',
    loadLess: 'Load less',
    betType: 'Bet Type',
    sortEventTime: 'Event Time',
    sortSubEventBetTime: 'Event Time',
    middleBetLine: 'DRAW',
    sortEVHL: 'Expected Value High-Low',
    sortEVLH: 'Expected Value Low-High',
    sortAIProbHL: 'AI Prob High-Low',
    sortAIProbLH: 'AI Prob Low-High',
    sortImProbHL: 'Im. Prob Hi High-Low',
    sortImProbLH: 'Im. Prob Hi Low-High',
    sortBestOddsHL: 'Best Odds High-Low',
    sortBestOddsLH: 'Best Odds Low-High',
    sortHitRate5HL: 'Hit Rate (Last 5) High-Low',
    sortHitRate5LH: 'Hit Rate (Last 5) Low-High',
    sortHitRate10HL: 'Hit Rate (Last 10) High-Low',
    sortHitRate10LH: 'Hit Rate (Last 10) Low-High',
    sortHitRate20HL: 'Hit Rate (Last 20) High-Low',
    sortHitRate20LH: 'Hit Rate (Last 20) Low-High',
    sortBetPercentageHL: '% di scommesse alto - basso',
    sortBetPercentageLH: '% di scommesse basso - alto',
    sortStakePercentageHL: '% di distribuzione monetaria alto - basso',
    sortStakePercentageLH: '% di distribuzione monetaria basso - alto',
    sortStakeBetDiffHL: 'Differenza alto - basso',
    sortStakeBetDiffLH: 'Differenza basso - alto',
    matchCenterSearchPlaceholder: 'Search team name',
  },
  US: {
    bestMarket: 'Best {market}',
    bestLine: 'Best {line} Line',
    propsFilter: 'Bets',
    minimumEV: 'Minimum EV',
    minimumAIProb: 'Minimum AI Probability',
    minimumHitRate: 'Minimum Hit Rate (Last 10)',
    date: 'Dates',
    odds: 'Odds',
    bestOdds: 'Best Odds',
    team: 'Team',
    player: 'Player',
    loadMore: 'Load More',
    loadLess: 'Load Less',
    betType: 'Bet Type',
    sortEventTime: 'Event Time',
    sortSubEventBetTime: 'Event Time',
    middleBetLine: 'DRAW',
    sortEVHL: 'Expected Value High-Low',
    sortEVLH: 'Expected Value Low-High',
    sortAIProbHL: 'AI Prob High-Low',
    sortAIProbLH: 'AI Prob Low-High',
    sortImProbHL: 'Im. Prob Hi High-Low',
    sortImProbLH: 'Im. Prob Hi Low-High',
    sortBestOddsHL: 'Best Odds High-Low',
    sortBestOddsLH: 'Best Odds Low-High',
    sortHitRate5HL: 'Hit Rate (Last 5) High-Low',
    sortHitRate5LH: 'Hit Rate (Last 5) Low-High',
    sortHitRate10HL: 'Hit Rate (Last 10) High-Low',
    sortHitRate10LH: 'Hit Rate (Last 10) Low-High',
    sortHitRate20HL: 'Hit Rate (Last 20) High-Low',
    sortHitRate20LH: 'Hit Rate (Last 20) Low-High',
    sortBetPercentageHL: '% of Bets High-Low',
    sortBetPercentageLH: '% of Bets Low-High',
    sortStakePercentageHL: '% of Money High-Low',
    sortStakePercentageLH: '% of Money Low-High',
    sortStakeBetDiffHL: 'Difference High-Low',
    sortStakeBetDiffLH: 'Difference Low-High',
    matchCenterSearchPlaceholder: 'Search team name',
  },
  WS: {
    bestMarket: 'Best {market}',
    bestLine: 'Best {line} Line',
    propsFilter: 'Bets',
    minimumEV: 'Minimum EV',
    minimumAIProb: 'Minimum AI Probability',
    minimumHitRate: 'Minimum Hit Rate (Last 10)',
    date: 'Dates',
    odds: 'Odds',
    bestOdds: 'Odds',
    team: 'Team',
    player: 'Player',
    loadMore: 'Load More',
    loadLess: 'Load Less',
    betType: 'Bet Type',
    sortEventTime: 'Event Time',
    sortSubEventBetTime: 'Event Time',
    middleBetLine: 'DRAW',
    sortEVHL: 'Expected Value High-Low',
    sortEVLH: 'Expected Value Low-High',
    sortAIProbHL: 'AI Prob High-Low',
    sortAIProbLH: 'AI Prob Low-High',
    sortImProbHL: 'Im. Prob Hi High-Low',
    sortImProbLH: 'Im. Prob Hi Low-High',
    sortBestOddsHL: 'Best Odds High-Low',
    sortBestOddsLH: 'Best Odds Low-High',
    sortHitRate5HL: 'Hit Rate (Last 5) High-Low',
    sortHitRate5LH: 'Hit Rate (Last 5) Low-High',
    sortHitRate10HL: 'Hit Rate (Last 10) High-Low',
    sortHitRate10LH: 'Hit Rate (Last 10) Low-High',
    sortHitRate20HL: 'Hit Rate (Last 20) High-Low',
    sortHitRate20LH: 'Hit Rate (Last 20) Low-High',
    sortBetPercentageHL: '% of Bets High-Low',
    sortBetPercentageLH: '% of Bets Low-High',
    sortStakePercentageHL: '% of Money High-Low',
    sortStakePercentageLH: '% of Money Low-High',
    sortStakeBetDiffHL: 'Difference High-Low',
    sortStakeBetDiffLH: 'Difference Low-High',
    matchCenterSearchPlaceholder: 'Search team name',
  },
  // ES: {
  //   propsFilter: 'Propuestas',
  //   minimumEV: 'VE mínimo',
  //   minimumAIProb: 'Probabilidad mínima según IA',
  //   minimumHitRate: 'Tasa de acierto mínima (10 últimos)',
  //   date: 'Fechas',
  //   odds: 'Cuotas',
  //   bestOdds: 'Mejores cuotas',
  //   team: 'Equipo',
  //   player: 'Jugador',
  //   loadMore: 'Cargar más',
  //   loadLess: 'Cargar menos',
  //   betType: 'Tipo de apuesta',
  //   sortEventTime: 'Hora del evento',
  //   middleBetLine: 'EMPATE',
  //   sortEVHL: 'Valor Esperado - De mayor a menor',
  //   sortEVLH: 'Valor Esperado - De menor a mayor',
  //   sortAIProbHL: 'Probabilidad IA - De mayor a menor',
  //   sortAIProbLH: 'Probabilidad IA - De menor a mayor',
  //   sortImProbHL: 'Probabilidad implícita - De mayor a menor',
  //   sortImProbLH: 'Probabilidad implícita - De menor a mayor',
  //   sortBestOddsHL: 'Mejores cuotas - De mayor a menor',
  //   sortBestOddsLH: 'Mejores cuotas - De menor a mayor',
  //   sortHitRate5HL: 'Tasa de acierto (5 últimos) - De mayor a menor',
  //   sortHitRate5LH: 'Tasa de acierto (5 últimos) - De menor a mayor',
  //   sortHitRate10HL: 'Tasa de acierto (10 últimos) - De mayor a menor',
  //   sortHitRate10LH: 'Tasa de acierto (10 últimos) - De menor a mayor',
  //   sortHitRate20HL: 'Tasa de acierto (20 últimos) - De mayor a menor',
  //   sortHitRate20LH: 'Tasa de acierto (20 últimos) - De menor a mayor',
  // matchCenterSearchPlaceholder: 'Buscar equipo',

  // },
} satisfies Record<SearchSupportedRepubName, FiltersConfig>;
